import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const CartContext = createContext(null);
const STORAGE_KEY = 'vedamarga_cart';

const buildUid = (item) => {
  const addonKey = (item.addons || [])
    .map((addon) => `${addon.value}:${addon.price}`)
    .sort()
    .join('|');
  return `${item.id}-${item.packageName || 'default'}-${addonKey}`;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    if (typeof window === 'undefined') return [];
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      console.error('Unable to persist cart data', error);
    }
  }, [items]);

  const addItem = (item) => {
    const uid = buildUid(item);
    setItems((prevItems) => {
      const existing = prevItems.find((existingItem) => existingItem.uid === uid);
      if (existing) {
        return prevItems.map((existingItem) =>
          existingItem.uid === uid
            ? { ...existingItem, quantity: existingItem.quantity + (item.quantity || 1) }
            : existingItem
        );
      }
      return [...prevItems, { ...item, uid, quantity: item.quantity || 1 }];
    });
  };

  const removeItem = (uid) => {
    setItems((prevItems) => prevItems.filter((item) => item.uid !== uid));
  };

  const updateQuantity = (uid, quantity) => {
    setItems((prevItems) =>
      prevItems.map((item) =>
        item.uid === uid ? { ...item, quantity: Math.max(1, quantity) } : item
      )
    );
  };

  const clearCart = () => {
    setItems([]);
  };

  const cartCount = useMemo(
    () => items.reduce((total, item) => total + (item.quantity || 0), 0),
    [items]
  );

  const cartTotal = useMemo(
    () =>
      items.reduce((total, item) => {
        const addonTotal = (item.addons || []).reduce(
          (sum, addon) => sum + (Number(addon.price) || 0),
          0
        );
        return total + ((Number(item.price) || 0) + addonTotal) * (item.quantity || 1);
      }, 0),
    [items]
  );

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        cartCount,
        cartTotal,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};
